import {
  ExtratoResponseSchema,
  ExtratoPageResponseSchema,
  MovimentacaoDetailDtoSchema,
  PixWithdrawalDtoSchema,
  PixWithdrawalListResponseSchema,
  SettlementResponseSchema,
  TopupCreatedResponseSchema,
  TopupDtoSchema,
  ListTopupsResponseSchema,
  PagoComSaldoResponseSchema,
  type ExtratoFilter,
  type ExtratoResponse,
  type ExtratoPageResponse,
  type MovimentacaoDetailDto,
  type PixWithdrawalDto,
  type PixWithdrawalListResponse,
  type SacarComChavePadraoRequest,
  type SacarRequest,
  type SettlementResponse,
  type TopupCreatedResponse,
  type TopupDto,
  type ListTopupsQuery,
  type ListTopupsResponse,
  type PagoComSaldoResponse,
} from '@lance-x/shared';
import type { ApiClient } from '../index';

export class CarteiraEndpoints {
  constructor(private readonly client: ApiClient) {}

  me(): Promise<ExtratoResponse> {
    return this.client.get('/carteira/me', {
      responseSchema: ExtratoResponseSchema,
    });
  }

  extrato(filter?: ExtratoFilter): Promise<ExtratoResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (filter?.tipo !== undefined) query['tipo'] = filter.tipo;
    // Phase 28 v1.6 — REQ-EXTRATO-01 (D-01). Status is orthogonal to tipo
    // (Pitfall 6). Backend ExtratoFilterDto already accepts it (28-01).
    if (filter?.status !== undefined) query['status'] = filter.status;
    if (filter?.dataDe !== undefined) query['dataDe'] = filter.dataDe;
    if (filter?.dataAte !== undefined) query['dataAte'] = filter.dataAte;
    if (filter?.page !== undefined) query['page'] = filter.page;
    if (filter?.limit !== undefined) query['limit'] = filter.limit;
    return this.client.get('/carteira/me/movimentacoes', {
      query,
      responseSchema: ExtratoResponseSchema,
    });
  }

  /**
   * Phase 28 v1.6 — REQ-EXTRATO-01 (D-03). Recibo detail endpoint.
   * Returns 404 (not 403) when movId belongs to another carteira (IDOR-safe;
   * Pattern 3 from 28-RESEARCH.md). Backend service emits the bruto/taxa/
   * liquido derivation per tipo + actorId/actorNome raw (frontend applies
   * renderActorName for the LGPD anonymization fallback).
   */
  getMovimentacao(id: string): Promise<MovimentacaoDetailDto> {
    return this.client.get(`/carteira/movimentacoes/${id}`, {
      responseSchema: MovimentacaoDetailDtoSchema,
    });
  }

  finalizarEvento(eventoId: string): Promise<SettlementResponse> {
    return this.client.post(`/eventos/${eventoId}/finalizar`, undefined, {
      responseSchema: SettlementResponseSchema,
    });
  }

  // ---------- Phase 19 v1.4 — PIX-03 saque ----------

  /**
   * Request a PIX withdrawal. Backend validates saldo + minimum (R$ 10) and
   * emits the Pagar.me transfer; the returned `PixWithdrawalDto` shows
   * `status='pendente'` until the webhook resolves it.
   */
  sacar(input: SacarRequest): Promise<PixWithdrawalDto> {
    return this.client.post('/carteira/sacar', input, {
      responseSchema: PixWithdrawalDtoSchema,
    });
  }

  /**
   * Phase 24 v1.6 — REQ-PIX-01 / D-08 Path 2. Saque using the profissional's
   * default PIX chave resolved server-side. Body carries ONLY `{ valorCents }` —
   * plaintext chave is NEVER sent from the client.
   *
   * Returns 422 `sem_chave_padrao` when the profissional has no default
   * registered. Caller should display "Cadastre uma chave PIX padrão primeiro"
   * toast and route to `/profissional/carteira/dados-bancarios`.
   */
  sacarComChavePadrao(
    input: SacarComChavePadraoRequest,
  ): Promise<PixWithdrawalDto> {
    return this.client.post('/carteira/sacar/com-chave-padrao', input, {
      responseSchema: PixWithdrawalDtoSchema,
    });
  }

  /** Profissional saque history list (last 100, most recent first). */
  listSaques(): Promise<PixWithdrawalListResponse> {
    return this.client.get('/carteira/saques', {
      responseSchema: PixWithdrawalListResponseSchema,
    });
  }

  // ---------- Phase 27 v1.6 — REQ-WALLET-01 (D-08..D-10, D-14) ----------

  /**
   * POST /carteira/wallet/topup — create a PIX recarga charge.
   * Body validated by shared CreateTopupRequestSchema (PM-4 range + step).
   * Returns the topup id + Pagar.me PIX QR + expiraEm.
   */
  createTopup(input: { valorCents: number }): Promise<TopupCreatedResponse> {
    return this.client.post('/carteira/wallet/topup', input, {
      responseSchema: TopupCreatedResponseSchema,
    });
  }

  /** GET /carteira/wallet/topup/:id — scoped to actor empresa; 404 on cross-empresa. */
  getTopup(id: string): Promise<TopupDto> {
    return this.client.get(`/carteira/wallet/topup/${id}`, {
      responseSchema: TopupDtoSchema,
    });
  }

  /** GET /carteira/wallet/topups — paginated list scoped to actor empresa. */
  listTopups(query?: ListTopupsQuery): Promise<ListTopupsResponse> {
    const q: Record<string, string | number | undefined> = {};
    if (query?.status !== undefined) q['status'] = query.status;
    if (query?.page !== undefined) q['page'] = query.page;
    if (query?.pageSize !== undefined) q['pageSize'] = query.pageSize;
    return this.client.get('/carteira/wallet/topups', {
      query: q,
      responseSchema: ListTopupsResponseSchema,
    });
  }

  /** POST /carteira/wallet/topup/:id/reembolsar — automated PIX refund of unused topup balance. */
  refundTopup(id: string): Promise<TopupDto> {
    return this.client.post(`/carteira/wallet/topup/${id}/reembolsar`, undefined, {
      responseSchema: TopupDtoSchema,
    });
  }

  /**
   * POST /carteira/wallet/pay-evento/:eventoId — atomic pay-with-saldo (D-14).
   * Returns 422 saldo_insuficiente if balance is insufficient at the moment of
   * the conditional UPDATE (TOCTOU-safe).
   */
  payEventoComSaldo(eventoId: string): Promise<PagoComSaldoResponse> {
    return this.client.post(`/carteira/wallet/pay-evento/${eventoId}`, undefined, {
      responseSchema: PagoComSaldoResponseSchema,
    });
  }

  /**
   * Phase 32 — DEBT-04 cursor-paginated extrato.
   * GET /carteira/extrato/page
   * Returns { carteira, movimentacoes: MovimentacaoDto[], nextCursor: string|null }.
   * Pass nextCursor as cursor on the next call to fetch the following page.
   */
  extratoCursor(opts?: {
    cursor?: string;
    limit?: number;
    tipo?: string;
    status?: string;
    dataDe?: string;
    dataAte?: string;
  }): Promise<ExtratoPageResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (opts?.cursor !== undefined) query['cursor'] = opts.cursor;
    if (opts?.limit !== undefined) query['limit'] = opts.limit;
    if (opts?.tipo !== undefined) query['tipo'] = opts.tipo;
    if (opts?.status !== undefined) query['status'] = opts.status;
    if (opts?.dataDe !== undefined) query['dataDe'] = opts.dataDe;
    if (opts?.dataAte !== undefined) query['dataAte'] = opts.dataAte;
    return this.client.get('/carteira/extrato/page', {
      query,
      responseSchema: ExtratoPageResponseSchema,
    });
  }
}
